import { ModalProps as AntdModalProps, Modal as AntdModal } from "antd";
import hashSum from "hash-sum";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { modalPositionsStore } from "./ModalPositionsStore";
import { DndContext, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { Coordinates, DragEndEvent } from "@dnd-kit/core/dist/types";
import { useState } from "react";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import classnames from "classnames";
import { SmartPointerSensor } from "utils/libHelpers/SmartPointerSensor";

interface ModalProps extends AntdModalProps {
  enableDnD?: boolean;
}

export const Modal = observer(({ children, enableDnD, ...modalProps }: ModalProps) => {
  const [uid, setUid] = useState<string>();
  // const activationConstraint = { distance: { x: 2, y: 2 } };
  const activationConstraint = { delay: 300, tolerance: 5 };
  const smartPointerSensor = useSensor(SmartPointerSensor, { activationConstraint });

  const sensors = useSensors(smartPointerSensor);
  const [{ x, y }, setCoordinates] = useState<Coordinates>({
    x: 0,
    y: 0,
  });

  const setInitialCoords = () => {
    const uid = makeModalUid(modalProps);
    setUid(uid);
    const position = modalPositionsStore.getPosition(uid);
    if (position) {
      setCoordinates({ x: position.left, y: position.top });
    }
  };

  useEffect(() => {
    setInitialCoords();
  }, []);

  const handleDragEnd = ({ delta, activatorEvent }: DragEndEvent) => {
    activatorEvent.preventDefault();
    const newTop = y + delta.y;
    const newLeft = x + delta.x;
    modalPositionsStore.setPosition(uid, { top: newTop, left: newLeft });
    setCoordinates({
      x: newLeft,
      y: newTop,
    });
  };
  return (
    <DndContext sensors={sensors} modifiers={[restrictToWindowEdges]} id={uid} onDragEnd={handleDragEnd}>
      <DraggableModal uid={uid} modalProps={modalProps} top={y} left={x} enableDnD={enableDnD}>
        {children}
      </DraggableModal>
    </DndContext>
  );
});

function makeModalUid(modalProps: AntdModalProps = {}) {
  const flatModalProps = Object.keys(modalProps).reduce((buffer, key) => {
    if (modalProps[key] !== null && typeof modalProps[key] === "object") {
      buffer += Object.keys(modalProps[key]).reduce((buff, k) => {
        if (modalProps[key][k]?.toString) {
          buff += modalProps[key][k].toString() + "-";
        }
        return buff;
      }, key);
    } else if (modalProps[key]?.toString) {
      buffer += modalProps[key].toString() + "-";
    }
    return buffer;
  }, "");
  const modalPropsHash = hashSum(flatModalProps);
  return `modal-${modalPropsHash}`;
}

/** Omits circular structure when converting object to json */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stringify(obj: any) {
  let cache = [];
  const str = JSON.stringify(obj, function (key, value) {
    if (typeof value === "bigint") {
      return;
    }
    if (typeof value === "object" && value !== null) {
      if (cache.indexOf(value) !== -1) {
        // Circular reference found, discard key
        return;
      }
      // Store value in our collection
      cache.push(value);
    }
    return value;
  });
  cache = null; // reset the cache
  return str;
}

function DraggableModal({ modalProps, children, uid, top, left, enableDnD }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: uid,
    disabled: !enableDnD,
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;
  return (
    <AntdModal
      {...modalProps}
      modalRender={(modalComponent) => (
        <div
          ref={setNodeRef}
          style={{ ...style, position: "relative", top: top, left: left }}
          {...attributes}
          {...listeners}
          className={classnames("draggable-modal", { "is-dragging": isDragging })}
        >
          {modalComponent}
        </div>
      )}
    >
      {children}
    </AntdModal>
  );
}
